import React, { useState, useEffect } from 'react';
import PatientSidebar from './PatientSidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarAlt, faClock, faUser, faBuilding, faHandshake, 
    faVideo, faMapMarkerAlt, faBell, faCheckCircle, faTimesCircle,
    faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from '../utils/utils';
import localForage from 'localforage';

interface Appointment {
    id: string;
    title: string;
    description?: string;
    doctor_name: string;
    clinic_name: string;
    appointment_date: string;
    appointment_time: string;
    duration: number; // in minutes
    type: 'independent' | 'collaboration';
    status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
    appointment_method: 'physical' | 'telehealth' | 'hybrid';
    location?: string;
    telehealth_link?: string;
    collaboration_id?: string;
    collaboration_name?: string;
    task_name?: string;
    reminder_sent: boolean;
    notes?: string;
    created_at: string;
    updated_at: string;
}

interface AppointmentStats {
    total: number;
    upcoming: number;
    completed: number;
    cancelled: number;
    independent: number;
    collaboration: number;
}

const PatientAppointments: React.FC = () => {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [stats, setStats] = useState<AppointmentStats>({
        total: 0,
        upcoming: 0,
        completed: 0,
        cancelled: 0,
        independent: 0,
        collaboration: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [activeTab, setActiveTab] = useState<'all' | 'independent' | 'collaboration'>('all');
    const [patientId, setPatientId] = useState<string | null>(null);
    const baseUrl = useBaseUrl();

    useEffect(() => {
        document.title = "My Appointments - Uzima Healthcare";
        initializeAppointments();
    }, []);

    const initializeAppointments = async () => {
        try {
            setLoading(true);
            
            // Get patient ID from localForage
            const storedPatientId = await localForage.getItem("patient_id");
            if (!storedPatientId) {
                throw new Error('Patient ID not found');
            }
            setPatientId(storedPatientId as string);
            
            // Fetch appointments from backend
            await fetchAppointments(storedPatientId as string);
            
        } catch (error) {
            console.error('Error initializing appointments:', error);
            setError('Failed to load appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAppointments = async (patientId: string) => {
        try {
            const response = await fetch(`${baseUrl}/api/patient/appointments/?patient_id=${patientId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAppointments(data.appointments || []);
            setStats(data.stats || {
                total: 0,
                upcoming: 0,
                completed: 0,
                cancelled: 0,
                independent: 0,
                collaboration: 0
            });

        } catch (error) {
            console.error('Error fetching appointments:', error);
            setError('Failed to fetch appointments from server.');
        }
    };

    const getFilteredAppointments = () => {
        let filtered = appointments;
        
        // Filter by type
        if (activeTab === 'independent') {
            filtered = filtered.filter(apt => apt.type === 'independent');
        } else if (activeTab === 'collaboration') {
            filtered = filtered.filter(apt => apt.type === 'collaboration');
        }
        
        // Filter by selected date
        const selectedDateStr = selectedDate.toISOString().split('T')[0];
        filtered = filtered.filter(apt => apt.appointment_date === selectedDateStr);
        
        return filtered.sort((a, b) => new Date(a.appointment_time).getTime() - new Date(b.appointment_time).getTime());
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            scheduled: { color: 'warning', icon: faClock, text: 'Scheduled' },
            confirmed: { color: 'info', icon: faCheckCircle, text: 'Confirmed' },
            completed: { color: 'success', icon: faCheckCircle, text: 'Completed' },
            cancelled: { color: 'danger', icon: faTimesCircle, text: 'Cancelled' },
            no_show: { color: 'secondary', icon: faExclamationTriangle, text: 'No Show' }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled;
        
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} fw-bold`}>
                <FontAwesomeIcon icon={config.icon} className="me-1" size="sm" />
                {config.text}
            </span>
        );
    };

    const getMethodBadge = (method: string) => {
        const methodConfig = {
            physical: { color: 'primary', icon: faMapMarkerAlt, text: 'Physical' },
            telehealth: { color: 'info', icon: faVideo, text: 'Telehealth' },
            hybrid: { color: 'warning', icon: faInfoCircle, text: 'Hybrid' }
        };
        
        const config = methodConfig[method as keyof typeof methodConfig] || methodConfig.physical;
        
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} fw-bold`}>
                <FontAwesomeIcon icon={config.icon} className="me-1" size="sm" />
                {config.text}
            </span>
        );
    };

    const getTypeBadge = (type: string) => {
        const typeConfig = {
            independent: { color: 'success', icon: faUser, text: 'Independent' },
            collaboration: { color: 'warning', icon: faHandshake, text: 'Collaboration' }
        };
        
        const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.independent;
        
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} fw-bold`}>
                <FontAwesomeIcon icon={config.icon} className="me-1" size="sm" />
                {config.text}
            </span>
        );
    };

    const joinTelehealthSession = (appointment: Appointment) => {
        if (appointment.appointment_method === 'telehealth' && appointment.telehealth_link) {
            window.open(appointment.telehealth_link, '_blank');
        }
    };

    const refreshAppointments = () => {
        if (patientId) {
            fetchAppointments(patientId);
        }
    };

    if (loading) {
        return (
            <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-3">Loading appointments...</div>
                    </div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
                <PatientSidebar />
                <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <div className="text-center py-5">
                        <div className="alert alert-danger" role="alert">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </div>
                        <button className="btn btn-primary mt-3" onClick={refreshAppointments}>
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Try Again
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    const filteredAppointments = getFilteredAppointments();
    const selectedDateStr = selectedDate.toISOString().split('T')[0];

    return (
        <div className="d-flex" style={{ minHeight: '100vh', background: '#fafbfc' }}>
            <PatientSidebar />
            <main className="flex-grow-1 px-4 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
                {/* Header */}
                <div className="mb-4">
                    <h2 className="fw-bold mb-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                        My Appointments
                    </h2>
                    <div className="text-muted fs-5">Manage your healthcare appointments and consultations</div>
                </div>

                {/* Stats Cards */}
                <div className="row g-3 mb-4">
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <div className="fw-bold fs-4 text-primary">{stats.total}</div>
                            <div className="text-muted small">Total</div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <div className="fw-bold fs-4 text-success">{stats.upcoming}</div>
                            <div className="text-muted small">Upcoming</div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <div className="fw-bold fs-4 text-info">{stats.completed}</div>
                            <div className="text-muted small">Completed</div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <div className="fw-bold fs-4 text-warning">{stats.independent}</div>
                            <div className="text-muted small">Independent</div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <div className="fw-bold fs-4 text-secondary">{stats.collaboration}</div>
                            <div className="text-muted small">Collaboration</div>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div className="card border-0 shadow-sm text-center py-3">
                            <button className="btn btn-outline-primary btn-sm" onClick={refreshAppointments}>
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Appointments List */}
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Appointments for {selectedDate.toLocaleDateString()}</h5>
                                    <div className="btn-group btn-group-sm">
                                        <button 
                                            className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setActiveTab('all')}
                                        >
                                            All
                                        </button>
                                        <button 
                                            className={`btn ${activeTab === 'independent' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setActiveTab('independent')}
                                        >
                                            Independent
                                        </button>
                                        <button 
                                            className={`btn ${activeTab === 'collaboration' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setActiveTab('collaboration')}
                                        >
                                            Collaboration
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {filteredAppointments.length === 0 ? (
                                    <div className="text-center py-4">
                                        <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-muted mb-2" />
                                        <p className="text-muted">No appointments for this date</p>
                                        <small className="text-muted">Select a different date or check another appointment type</small>
                                    </div>
                                ) : (
                                    <div className="appointments-list">
                                        {filteredAppointments.map((appointment) => (
                                            <div key={appointment.id} className="appointment-item border rounded p-3 mb-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1">{appointment.title}</h6>
                                                        <div className="text-muted small mb-2">
                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            {appointment.doctor_name} • {appointment.clinic_name}
                                                        </div>
                                                        <div className="d-flex gap-2 mb-2">
                                                            {getTypeBadge(appointment.type)}
                                                            {getMethodBadge(appointment.appointment_method)}
                                                            {getStatusBadge(appointment.status)}
                                                        </div>
                                                        {appointment.type === 'collaboration' && (
                                                            <div className="text-info small">
                                                                <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                                                Collaboration: {appointment.collaboration_name}
                                                                {appointment.task_name && (
                                                                    <span className="ms-2">• Task: {appointment.task_name}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold text-primary">
                                                            {new Date(appointment.appointment_time).toLocaleTimeString([], { 
                                                                hour: '2-digit', 
                                                                minute: '2-digit' 
                                                            })}
                                                        </div>
                                                        <div className="text-muted small">
                                                            Duration: {appointment.duration} min
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {appointment.description && (
                                                    <div className="text-muted small mb-2">
                                                        {appointment.description}
                                                    </div>
                                                )}
                                                
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex gap-2">
                                                        {appointment.appointment_method === 'telehealth' && appointment.telehealth_link && (
                                                            <button 
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => joinTelehealthSession(appointment)}
                                                            >
                                                                <FontAwesomeIcon icon={faVideo} className="me-1" />
                                                                Join Session
                                                            </button>
                                                        )}
                                                        {appointment.appointment_method === 'physical' && appointment.location && (
                                                            <span className="text-muted small">
                                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                                                {appointment.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="d-flex gap-2">
                                                        {appointment.reminder_sent && (
                                                            <span className="badge bg-success bg-opacity-10 text-success">
                                                                <FontAwesomeIcon icon={faBell} className="me-1" />
                                                                Reminder Sent
                                                            </span>
                                                        )}
                                                        <small className="text-muted">
                                                            Created: {new Date(appointment.created_at).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Calendar Sidebar */}
                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                    Calendar
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="calendar-wrapper">
                                    <input 
                                        type="date" 
                                        className="form-control mb-3"
                                        value={selectedDateStr}
                                        onChange={(e) => setSelectedDate(new Date(e.target.value))}
                                    />
                                    
                                    <div className="calendar-legend">
                                        <div className="d-flex align-items-center mb-2">
                                            <span className="badge bg-success bg-opacity-10 text-success me-2">●</span>
                                            <small>Independent Appointments</small>
                                        </div>
                                        <div className="d-flex align-items-center mb-2">
                                            <span className="badge bg-warning bg-opacity-10 text-warning me-2">●</span>
                                            <small>Collaboration Appointments</small>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-info bg-opacity-10 text-info me-2">●</span>
                                            <small>Telehealth Sessions</small>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-3">
                                        <h6 className="text-muted">Quick Stats</h6>
                                        <div className="row text-center">
                                            <div className="col-6">
                                                <div className="fw-bold text-primary">{stats.upcoming}</div>
                                                <small className="text-muted">Upcoming</small>
                                            </div>
                                            <div className="col-6">
                                                <div className="fw-bold text-success">{stats.completed}</div>
                                                <small className="text-muted">Completed</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <style>{`
                .appointment-item {
                    transition: all 0.2s ease;
                }
                
                .appointment-item:hover {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    transform: translateY(-1px);
                }
                
                .calendar-legend {
                    font-size: 0.875rem;
                }
                
                .badge {
                    font-size: 0.75rem;
                }
            `}</style>
        </div>
    );
};

export default PatientAppointments;
